import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import PropTypes from 'prop-types';
import { DRAG_AREA } from './constants';
import NestableListBase from 'wix-style-react/dist/es/src/NestableListBase';
import TreeSectionDivider from '../treeSectionDivider/treeSectionDivider';
import TreeListRow from './TreeListRow/TreeListRow';
import { st, classes } from './TreeList.st.css';
import CustomScroll from 'react-custom-scroll';
var TreeList = function TreeList(props) {
  var className = props.className,
    dataHook = props.dataHook,
    items = props.items,
    maxDepth = props.maxDepth,
    zIndex = props.zIndex,
    readOnly = props.readOnly,
    dragArea = props.dragArea,
    dragHandleVisibility = props.dragHandleVisibility,
    onChange = props.onChange,
    onDragStart = props.onDragStart,
    onDragEnd = props.onDragEnd,
    scrollable = props.scrollable;
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isDragging = _React$useState2[0],
    setIsDragging = _React$useState2[1];
  var renderItemRow = function renderItemRow(_ref) {
    var connectDragSource = _ref.connectDragSource,
      item = _ref.item,
      isPreview = _ref.isPreview,
      isPlaceholder = _ref.isPlaceholder,
      depth = _ref.depth,
      showCollapseArrow = _ref.showCollapseArrow;
    return /*#__PURE__*/React.createElement(TreeListRow, {
      item: item,
      depth: depth - 1,
      readOnly: readOnly,
      isPlaceholder: isPlaceholder,
      isPreview: isPreview,
      isDragging: isDragging,
      dragArea: dragArea,
      dragHandleVisibility: dragHandleVisibility,
      connectDragSource: connectDragSource,
      showCollapseArrow: showCollapseArrow
    });
  };
  var _onDragStart = function _onDragStart(event) {
    // relevant for dragged item styles
    setIsDragging(true);
    onDragStart(event.item);
  };
  var _onDragEnd = function _onDragEnd(event) {
    // relevant for dragged item styles
    setIsDragging(false);
    onDragEnd(event.item);
  };

  /**
   * renders a single tree
   */
  var renderTreeList = function renderTreeList(treeList, index) {
    var id = treeList.id,
      label = treeList.label,
      dataHook = treeList.dataHook,
      isCollapsed = treeList.isCollapsed,
      onCollapseClick = treeList.onCollapseClick,
      suffix = treeList.suffix,
      children = treeList.children,
      showChildrenCollapseArrow = treeList.showChildrenCollapseArrow,
      hideTopBorder = treeList.hideTopBorder,
      hideBottomBorder = treeList.hideBottomBorder,
      _treeList$alwaysShowS = treeList.alwaysShowSuffix,
      alwaysShowSuffix = _treeList$alwaysShowS === void 0 ? false : _treeList$alwaysShowS;
    return /*#__PURE__*/React.createElement("div", {
      key: id,
      "data-hook": dataHook || "tree-list-section-root-".concat(id),
      className: classes.treeListSectionRoot,
      style: scrollable && !isCollapsed && items.length ? {
        height: "".concat(100 / items.length, "%")
      } : null
    }, /*#__PURE__*/React.createElement(TreeSectionDivider, {
      label: label,
      dataHook: "tree-list-section-divider",
      collapsed: isCollapsed !== null && isCollapsed !== void 0 ? isCollapsed : false,
      onClick: function onClick() {
        return onCollapseClick && onCollapseClick(id);
      },
      suffix: suffix,
      alwaysShowSuffix: alwaysShowSuffix,
      hideTopBorder: hideTopBorder,
      hideBottomBorder: hideBottomBorder
    }), !isCollapsed && /*#__PURE__*/React.createElement(CustomScroll, {
      flex: "1"
    }, /*#__PURE__*/React.createElement(NestableListBase, {
      isRenderDraggingChildren: false,
      useDragHandle: dragArea === DRAG_AREA.HANDLE,
      childrenStyle: {
        paddingLeft: 20
      },
      readOnly: readOnly,
      maxDepth: maxDepth,
      zIndex: zIndex,
      threshold: 20,
      items: children,
      renderItem: function renderItem(itemProps) {
        return renderItemRow(_objectSpread(_objectSpread({}, itemProps), {}, {
          showCollapseArrow: showChildrenCollapseArrow
        }));
      },
      onUpdate: function onUpdate(args) {
        onChange({
          movedItem: args.item,
          sectionItems: args.items,
          sectionIndex: index
        });
      },
      onDragStart: _onDragStart,
      onDragEnd: _onDragEnd,
      dataHook: "tree-list-section-items"
    })));
  };
  return /*#__PURE__*/React.createElement("div", {
    className: st(classes.root, {
      scrollable: scrollable
    }, className),
    "data-hook": dataHook
  }, items.map(renderTreeList));
};
TreeList.displayName = 'TreeList';
TreeList.propTypes = {
  /** applies a data-hook HTML attribute that can be used in the tests */
  dataHook: PropTypes.string,
  /** a css class to be applied to the component's root element */
  className: PropTypes.string,
  /**
   *  Items in tree list
   *  - `id` a unique string
   *  - `label` a label at the TreeListSection
   *  - `dataHook` applies a data-hook to group root
   *  - `isCollapsed` whether to collapse section
   *  - `onCollapseClick` handle collapse arrow click
   *  - `suffix` section divider suffix
   *  - `children` a tree list item props
   *  - `showChildrenCollapseArrow` show collapse arrow for the children
   *  - `hideTopBorder` hide section divider's top border
   *  - `hideBottomBorder` hide section divider's bottom border
   *
   *  Tree list item
   *  - `id` a unique string
   *  - `label` node or string
   *  - `className`
   *  - `dataHook` applies a data-hook to item root
   *  - `selected` <TreeListItem /> prop
   *  - `shouldScrollOnMount` scroll to item when mount
   *  - `isCollapsed` whether to collapse the tree list item
   *  - `draggable` allow to disable drag
   *  - `preventItemChangeDepth` keep item only on current level
   *  - `lockDropArea` prevent dragging in and out a nested item
   *  - `onClick` handle full row click
   *  - `onDoubleClick` handle label double click
   *  - `onCollapseClick` handle collapse arrow click
   *  - `icon` prefix icon node
   *  - `suffix` suffix node
   *  - `alwaysShowSuffix`
   *  - `children` nested tree list items
   *  - `disabled` disable the tree list item
   **/
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    dataHook: PropTypes.string,
    isCollapsed: PropTypes.bool,
    onCollapseClick: PropTypes.func,
    suffix: PropTypes.node,
    alwaysShowSuffix: PropTypes.bool,
    showChildrenCollapseArrow: PropTypes.bool,
    hideTopBorder: PropTypes.bool,
    hideBottomBorder: PropTypes.bool,
    children: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      dataHook: PropTypes.string,
      className: PropTypes.string,
      draggable: PropTypes.bool,
      selected: PropTypes.bool,
      shouldScrollOnMount: PropTypes.bool,
      isCollapsed: PropTypes.bool,
      onClick: PropTypes.func,
      onDoubleClick: PropTypes.func,
      onCollapseClick: PropTypes.func,
      icon: PropTypes.node,
      indications: PropTypes.arrayOf(PropTypes.node),
      suffix: PropTypes.node,
      alwaysShowSuffix: PropTypes.bool,
      // passed to NestableListBase
      zIndex: PropTypes.number,
      preventItemChangeDepth: PropTypes.bool,
      lockDropArea: PropTypes.bool,
      disabled: PropTypes.bool
    })).isRequired
  })).isRequired,
  /** max depth of nesting */
  maxDepth: PropTypes.number,
  /** defines the zIndex of the draggable layer */
  zIndex: PropTypes.number,
  /** make the list readOnly (disables drag) */
  readOnly: PropTypes.bool,
  /** choose the drag area the item */
  dragArea: PropTypes.oneOf(['all', 'handle']),
  /** visibility handle condition */
  dragHandleVisibility: PropTypes.oneOf(['always', 'hover']),
  /** allow scroll on items' children */
  scrollable: PropTypes.bool,
  /** callback for drag start */
  onDragStart: PropTypes.func,
  /** callback for drag end */
  onDragEnd: PropTypes.func,
  /** Triggers function when the item’s order or nesting position is changed. */
  onChange: PropTypes.func
};
TreeList.defaultProps = {
  maxDepth: Infinity,
  readOnly: false,
  dragArea: 'all',
  dragHandleVisibility: 'always',
  scrollable: false,
  onDragStart: function onDragStart() {},
  onDragEnd: function onDragEnd() {},
  onChange: function onChange() {}
};
export default TreeList;